/**
 * CanvasKit Renderer POC
 *
 * Two tabs:
 * 1. Preview: CanvasKit renderer with sample SceneDocument blueprints
 * 2. Interactive editor: CanvasKit-based editor with selection, transforms, drag
 */
import { createFileRoute } from '@tanstack/react-router';
import { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Stack,
  Paper,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Chip,
  Tabs,
  Tab,
  Button,
  Divider,
  type SelectChangeEvent,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CanvasKitPreview } from '@/components/canvas-editor/CanvasKitPreview';
import {
  CanvasKitEditor,
  type CanvasKitEditorHandle,
} from '@/components/canvas-editor/CanvasKitEditor';
import { ScenePropertiesPanel } from '@/components/canvas-editor/ScenePropertiesPanel';
import type { SceneDocument } from '@/lib/scene-document';

export const Route = createFileRoute('/_authed/dashboard/test/canvaskit')({
  component: CanvasKitTestPage,
});

// ─── Sample Blueprints (SceneDocument) ────────────────────────────────────

const SAMPLE_SIMPLE: SceneDocument = {
  version: 2,
  width: 600,
  height: 300,
  nodes: {
    background: {
      id: 'background', type: 'RECTANGLE', name: 'background', parentId: null,
      x: 0, y: 0, width: 600, height: 300,
      fills: [{ type: 'SOLID', color: '#1a1a2e' }],
    },
    headline: {
      id: 'headline', type: 'TEXT', name: 'headline', parentId: null,
      x: 60, y: 80, width: 480, height: 50,
      text: 'Welcome to nexmail', fontSize: 42, fontFamily: 'Inter',
      fontWeight: 700, fills: [{ type: 'SOLID', color: '#FFFFFF' }], textAlign: 'center',
    },
    'cta-button': {
      id: 'cta-button', type: 'GROUP', name: 'cta-button', parentId: null,
      x: 200, y: 200, width: 200, height: 48,
      children: ['cta-bg', 'cta-label'],
    },
    'cta-bg': {
      id: 'cta-bg', type: 'RECTANGLE', parentId: 'cta-button',
      x: 0, y: 0, width: 200, height: 48,
      fills: [{ type: 'SOLID', color: '#4F46E5' }], cornerRadius: 24,
    },
    'cta-label': {
      id: 'cta-label', type: 'TEXT', parentId: 'cta-button',
      x: 10, y: 14, width: 180, height: 20,
      text: 'GET STARTED', fontSize: 14, fontFamily: 'Inter',
      fontWeight: 700, fills: [{ type: 'SOLID', color: '#FFFFFF' }], textAlign: 'center',
    },
  },
  rootChildren: ['background', 'headline', 'cta-button'],
};

const SAMPLE_MEDIUM: SceneDocument = {
  version: 2,
  width: 600,
  height: 500,
  nodes: {
    background: {
      id: 'background', type: 'RECTANGLE', name: 'background', parentId: null,
      x: 0, y: 0, width: 600, height: 500,
      fills: [{
        type: 'GRADIENT_LINEAR',
        stops: [
          { position: 0, color: '#0f0c29' },
          { position: 0.5, color: '#302b63' },
          { position: 1, color: '#24243e' },
        ],
      }],
    },
    'hero-image': {
      id: 'hero-image', type: 'IMAGE', name: 'hero-image', parentId: null,
      x: 50, y: 30, width: 500, height: 250,
      imageSrc: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&h=300&fit=crop',
      cornerRadius: 12,
    },
    headline: {
      id: 'headline', type: 'TEXT', name: 'headline', parentId: null,
      x: 50, y: 310, width: 500, height: 44,
      text: 'Spring Collection 2026', fontSize: 36, fontFamily: 'Inter',
      fontWeight: 700, fills: [{ type: 'SOLID', color: '#FFFFFF' }], textAlign: 'center',
    },
    subtitle: {
      id: 'subtitle', type: 'TEXT', name: 'subtitle', parentId: null,
      x: 80, y: 370, width: 440, height: 24,
      text: 'Discover our curated selection of seasonal favorites',
      fontSize: 16, fontFamily: 'Inter',
      fills: [{ type: 'SOLID', color: 'rgba(255,255,255,0.7)' }], textAlign: 'center',
    },
    divider: {
      id: 'divider', type: 'RECTANGLE', name: 'divider', parentId: null,
      x: 250, y: 420, width: 100, height: 3,
      fills: [{ type: 'SOLID', color: '#4F46E5' }], cornerRadius: 2,
    },
    'cta-text': {
      id: 'cta-text', type: 'TEXT', name: 'cta-text', parentId: null,
      x: 200, y: 450, width: 200, height: 20,
      text: 'SHOP NOW', fontSize: 14, fontFamily: 'Inter',
      fontWeight: 700, fills: [{ type: 'SOLID', color: '#4F46E5' }],
      textAlign: 'center', letterSpacing: 2,
    },
  },
  rootChildren: ['background', 'hero-image', 'headline', 'subtitle', 'divider', 'cta-text'],
};

const SAMPLE_COMPLEX: SceneDocument = {
  version: 2,
  width: 600,
  height: 600,
  nodes: {
    background: {
      id: 'background', type: 'RECTANGLE', name: 'background', parentId: null,
      x: 0, y: 0, width: 600, height: 600,
      fills: [{ type: 'SOLID', color: '#f8f7ff' }],
    },
    'wave-decoration': {
      id: 'wave-decoration', type: 'PATH', name: 'wave-decoration', parentId: null,
      x: 0, y: 0, width: 600, height: 100,
      pathData: 'M0,100 C150,20 350,180 600,100 L600,0 L0,0 Z',
      fills: [{ type: 'SOLID', color: '#4F46E5' }],
    },
    'section-hero': {
      id: 'section-hero', type: 'GROUP', name: 'section:hero', parentId: null,
      x: 0, y: 0, width: 600, height: 280, clipContent: true,
      children: ['hero-bg', 'headline', 'subheadline', 'cta-button'],
    },
    'hero-bg': {
      id: 'hero-bg', type: 'RECTANGLE', name: 'hero-bg', parentId: 'section-hero',
      x: 0, y: 0, width: 600, height: 280,
      fills: [{ type: 'SOLID', color: '#4F46E5' }],
    },
    headline: {
      id: 'headline', type: 'TEXT', name: 'headline', parentId: 'section-hero',
      x: 60, y: 60, width: 480, height: 52,
      text: 'EXCLUSIVE OFFER', fontSize: 44, fontFamily: 'Inter',
      fontWeight: 700, fills: [{ type: 'SOLID', color: '#FFFFFF' }],
      textAlign: 'center', letterSpacing: 4,
    },
    subheadline: {
      id: 'subheadline', type: 'TEXT', name: 'subheadline', parentId: 'section-hero',
      x: 100, y: 130, width: 400, height: 24,
      text: 'Members save an extra 20% this weekend', fontSize: 16, fontFamily: 'Inter',
      fills: [{ type: 'SOLID', color: 'rgba(255,255,255,0.8)' }], textAlign: 'center',
    },
    'cta-button': {
      id: 'cta-button', type: 'GROUP', name: 'cta-button', parentId: 'section-hero',
      x: 200, y: 190, width: 200, height: 48,
      children: ['cta-bg', 'cta-label'],
    },
    'cta-bg': {
      id: 'cta-bg', type: 'RECTANGLE', parentId: 'cta-button',
      x: 0, y: 0, width: 200, height: 48,
      fills: [{ type: 'SOLID', color: '#FFFFFF' }], cornerRadius: 24,
    },
    'cta-label': {
      id: 'cta-label', type: 'TEXT', parentId: 'cta-button',
      x: 10, y: 14, width: 180, height: 20,
      text: 'CLAIM OFFER', fontSize: 14, fontFamily: 'Inter',
      fontWeight: 700, fills: [{ type: 'SOLID', color: '#4F46E5' }], textAlign: 'center',
    },
    'stats-row': {
      id: 'stats-row', type: 'GROUP', name: 'stats-row', parentId: null,
      x: 30, y: 310, width: 160, height: 90,
      children: ['stats-row-bg', 'stats-row-value', 'stats-row-label'],
    },
    'stats-row-bg': {
      id: 'stats-row-bg', type: 'RECTANGLE', parentId: 'stats-row',
      x: 0, y: 0, width: 160, height: 90,
      fills: [{ type: 'SOLID', color: '#FFFFFF' }], cornerRadius: 8,
      strokes: [{ type: 'SOLID', color: '#e5e7eb' }], strokeWeight: 1,
    },
    'stats-row-value': {
      id: 'stats-row-value', type: 'TEXT', parentId: 'stats-row',
      x: 10, y: 15, width: 140, height: 34,
      text: '50K+', fontSize: 28, fontFamily: 'Inter',
      fontWeight: 700, fills: [{ type: 'SOLID', color: '#4F46E5' }], textAlign: 'center',
    },
    'stats-row-label': {
      id: 'stats-row-label', type: 'TEXT', parentId: 'stats-row',
      x: 10, y: 55, width: 140, height: 16,
      text: 'Happy Customers', fontSize: 11, fontFamily: 'Inter',
      fills: [{ type: 'SOLID', color: '#6b7280' }], textAlign: 'center',
    },
    'stats-row-2': {
      id: 'stats-row-2', type: 'GROUP', name: 'stats-row-2', parentId: null,
      x: 220, y: 310, width: 160, height: 90,
      children: ['stats-row-2-bg', 'stats-row-2-value', 'stats-row-2-label'],
    },
    'stats-row-2-bg': {
      id: 'stats-row-2-bg', type: 'RECTANGLE', parentId: 'stats-row-2',
      x: 0, y: 0, width: 160, height: 90,
      fills: [{ type: 'SOLID', color: '#FFFFFF' }], cornerRadius: 8,
      strokes: [{ type: 'SOLID', color: '#e5e7eb' }], strokeWeight: 1,
    },
    'stats-row-2-value': {
      id: 'stats-row-2-value', type: 'TEXT', parentId: 'stats-row-2',
      x: 10, y: 15, width: 140, height: 34,
      text: '4.9', fontSize: 28, fontFamily: 'Inter',
      fontWeight: 700, fills: [{ type: 'SOLID', color: '#4F46E5' }], textAlign: 'center',
    },
    'stats-row-2-label': {
      id: 'stats-row-2-label', type: 'TEXT', parentId: 'stats-row-2',
      x: 10, y: 55, width: 140, height: 16,
      text: 'Avg Rating', fontSize: 11, fontFamily: 'Inter',
      fills: [{ type: 'SOLID', color: '#6b7280' }], textAlign: 'center',
    },
    'stats-row-3': {
      id: 'stats-row-3', type: 'GROUP', name: 'stats-row-3', parentId: null,
      x: 410, y: 310, width: 160, height: 90,
      children: ['stats-row-3-bg', 'stats-row-3-value', 'stats-row-3-label'],
    },
    'stats-row-3-bg': {
      id: 'stats-row-3-bg', type: 'RECTANGLE', parentId: 'stats-row-3',
      x: 0, y: 0, width: 160, height: 90,
      fills: [{ type: 'SOLID', color: '#FFFFFF' }], cornerRadius: 8,
      strokes: [{ type: 'SOLID', color: '#e5e7eb' }], strokeWeight: 1,
    },
    'stats-row-3-value': {
      id: 'stats-row-3-value', type: 'TEXT', parentId: 'stats-row-3',
      x: 10, y: 15, width: 140, height: 34,
      text: '98%', fontSize: 28, fontFamily: 'Inter',
      fontWeight: 700, fills: [{ type: 'SOLID', color: '#4F46E5' }], textAlign: 'center',
    },
    'stats-row-3-label': {
      id: 'stats-row-3-label', type: 'TEXT', parentId: 'stats-row-3',
      x: 10, y: 55, width: 140, height: 16,
      text: 'Satisfaction', fontSize: 11, fontFamily: 'Inter',
      fills: [{ type: 'SOLID', color: '#6b7280' }], textAlign: 'center',
    },
    'decoration-star': {
      id: 'decoration-star', type: 'STAR', name: 'decoration-star', parentId: null,
      x: 540, y: 460, width: 56, height: 56,
      sides: 5, innerRadius: 12,
      fills: [{ type: 'SOLID', color: '#fbbf24' }], opacity: 0.6,
    },
    footer: {
      id: 'footer', type: 'TEXT', name: 'footer', parentId: null,
      x: 100, y: 540, width: 400, height: 18,
      text: 'Offer valid through April 7, 2026. Terms apply.', fontSize: 12, fontFamily: 'Inter',
      fills: [{ type: 'SOLID', color: '#9ca3af' }], textAlign: 'center',
    },
  },
  rootChildren: [
    'background', 'wave-decoration', 'section-hero',
    'stats-row', 'stats-row-2', 'stats-row-3',
    'decoration-star', 'footer',
  ],
};

const SAMPLE_BLUEPRINTS: Record<string, { label: string; sceneDoc: SceneDocument }> = {
  simple: { label: 'Simple -- Rect + Text + CTA', sceneDoc: SAMPLE_SIMPLE },
  medium: { label: 'Medium -- Image + Gradient + Text', sceneDoc: SAMPLE_MEDIUM },
  complex: { label: 'Complex -- Groups + Path + Star + Stats', sceneDoc: SAMPLE_COMPLEX },
};

// ─── Preview Tab ──────────────────────────────────────────────────────────

function PreviewTab() {
  const [selectedKey, setSelectedKey] = useState<string>('simple');
  const [jsonExpanded, setJsonExpanded] = useState(false);

  const { sceneDoc } = SAMPLE_BLUEPRINTS[selectedKey];

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>Blueprint:</Typography>
        <Select
          value={selectedKey}
          onChange={(e: SelectChangeEvent<string>) => setSelectedKey(e.target.value)}
          size="small"
          sx={{ minWidth: 320 }}
        >
          {Object.entries(SAMPLE_BLUEPRINTS).map(([key, { label }]) => (
            <MenuItem key={key} value={key}>{label}</MenuItem>
          ))}
        </Select>
        <Typography variant="caption" color="text.secondary">
          {sceneDoc.width} x {sceneDoc.height}px
        </Typography>
      </Stack>

      <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>CanvasKit (Skia WASM)</Typography>
          </Stack>
        </Box>
        <Box sx={{ p: 2 }}>
          <CanvasKitPreview sceneDocument={sceneDoc} width={sceneDoc.width} height={sceneDoc.height} />
        </Box>
      </Paper>

      <Accordion expanded={jsonExpanded} onChange={() => setJsonExpanded(!jsonExpanded)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>SceneDocument JSON</Typography>
            <Chip label={`${Object.keys(sceneDoc.nodes).length} nodes`} size="small" variant="outlined" />
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <TextField
            multiline fullWidth size="small"
            value={JSON.stringify(sceneDoc, null, 2)}
            slotProps={{ input: { readOnly: true, sx: { fontFamily: 'monospace', fontSize: '11px' } } }}
            maxRows={30}
          />
        </AccordionDetails>
      </Accordion>
    </>
  );
}

// ─── Editor Tab ───────────────────────────────────────────────────────────

function EditorTab() {
  const [selectedKey, setSelectedKey] = useState<string>('complex');
  const editorRef = useRef<CanvasKitEditorHandle>(null);

  const { sceneDoc } = SAMPLE_BLUEPRINTS[selectedKey];

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>Blueprint:</Typography>
        <Select
          value={selectedKey}
          onChange={(e: SelectChangeEvent<string>) => setSelectedKey(e.target.value)}
          size="small"
          sx={{ minWidth: 320 }}
        >
          {Object.entries(SAMPLE_BLUEPRINTS).map(([key, { label }]) => (
            <MenuItem key={key} value={key}>{label}</MenuItem>
          ))}
        </Select>

        <Divider orientation="vertical" flexItem />

        <Button size="small" variant="outlined" onClick={() => editorRef.current?.addRect()}>
          + Rect
        </Button>
        <Button size="small" variant="outlined" onClick={() => editorRef.current?.addEllipse()}>
          + Ellipse
        </Button>
        <Button size="small" variant="outlined" onClick={() => editorRef.current?.addText()}>
          + Text
        </Button>
        <Button size="small" variant="outlined" onClick={() => editorRef.current?.deleteSelected()}>
          Delete
        </Button>
        <Button size="small" variant="outlined" onClick={() => editorRef.current?.undo()}>
          Undo
        </Button>
        <Button size="small" variant="outlined" onClick={() => editorRef.current?.redo()}>
          Redo
        </Button>
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
        Click to select, shift-click to multi-select, drag to move, scroll to pan,
        ctrl+scroll to zoom, space+drag to pan, double-click to enter group, arrow keys to nudge.
      </Typography>

      <Stack
        direction="row"
        spacing={0}
        sx={{ height: 'calc(100vh - 280px)', minHeight: 500 }}
      >
        <Paper
          variant="outlined"
          sx={{ flex: 1, overflow: 'hidden' }}
        >
          <CanvasKitEditor
            key={selectedKey}
            ref={editorRef}
            initialDocument={sceneDoc}
            onDocumentChange={(doc) => {
              // Could wire up a JSON preview here
            }}
            onSelectionChange={(nodeId, node) => {
              if (nodeId) {
                console.log('Selected:', nodeId, node?.type);
              }
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: 280,
                height: '100%',
                overflowY: 'auto',
                zIndex: 10,
                bgcolor: 'background.paper',
                borderLeft: '1px solid',
                borderColor: 'divider',
              }}
            >
              <ScenePropertiesPanel />
            </Paper>
          </CanvasKitEditor>
        </Paper>
      </Stack>
    </>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────

function CanvasKitTestPage() {
  const [tab, setTab] = useState(1);

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          CanvasKit POC
        </Typography>
        <Chip label="Experimental" size="small" color="warning" variant="outlined" />
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Preview Comparison" />
        <Tab label="Interactive Editor" />
      </Tabs>

      {tab === 0 && <PreviewTab />}
      {tab === 1 && <EditorTab />}
    </Box>
  );
}
